import { useEffect, useRef } from 'react';
import { openRemixInsufficientCreditsModal, shouldUseRemixModal } from '@/components/RemixUpgradeModal/helper';
import globalEventEmitter from '@/modules/EventEmitter';
import { EVENTS } from '@/modules/EventEmitter/const';
import { useProjectStore } from '@/stores/project';
import { useRemixUpgradeModalStore } from '@/stores/remixUpgradeModal';
import { useUserStore } from '@/stores/user';

const DEBOUNCE_MS = 300;

/**
 * 监听 userCreditUpdate 中的 creditCount 从 ">0" 转变为 "0" 的边沿，自动弹出 RemixUpgradeModal。
 * 仅在满足前置条件（Free 用户 + remix 项目）时触发。
 */
export function useRemixUpgradeTrigger() {
  const userPoints = useUserStore(s => s.userPoints);
  const currentProject = useProjectStore(s => s.currentProjectDetail);
  const refresh = useRemixUpgradeModalStore(s => s.refresh);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchedRef = useRef(false);
  const currentProjectRef = useRef(currentProject);
  const refreshRef = useRef(refresh);
  const prevPointsRef = useRef<number | undefined>(userPoints);

  useEffect(() => {
    currentProjectRef.current = currentProject;
  }, [currentProject]);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (typeof prevPointsRef.current !== 'number' && typeof userPoints === 'number') {
      prevPointsRef.current = userPoints;
    }
  }, [userPoints]);

  useEffect(() => {
    const handleCreditUpdate = (creditCount: number) => {
      const project = currentProjectRef.current;
      if (!shouldUseRemixModal(project)) {
        prevPointsRef.current = creditCount;
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      const prev = prevPointsRef.current;
      prevPointsRef.current = creditCount;

      const justRanOut = typeof prev === 'number' && prev > 0 && creditCount === 0;
      if (!justRanOut) {
        return;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(async () => {
        timerRef.current = null;
        if (!shouldUseRemixModal(currentProjectRef.current)) {
          return;
        }
        if (lastFetchedRef.current) {
          return;
        }
        lastFetchedRef.current = true;
        try {
          const ok = await refreshRef.current();
          if (ok && shouldUseRemixModal(currentProjectRef.current)) {
            openRemixInsufficientCreditsModal();
          }
        } catch (e) {
          console.error('useRemixUpgradeTrigger: refresh failed', e);
        } finally {
          lastFetchedRef.current = false;
        }
      }, DEBOUNCE_MS);
    };

    globalEventEmitter.on(EVENTS.PAYMENT_CREDIT_UPDATE, handleCreditUpdate);

    return () => {
      globalEventEmitter.off(EVENTS.PAYMENT_CREDIT_UPDATE, handleCreditUpdate);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
}
